import { markRaw } from 'vue';
import type { Router, RouteRecordRaw } from 'vue-router';
import { useGamePlayStore } from '@/stores/gamePlayStore';
import { useGameStore } from '@/stores/gameStore';
import { useUserStore } from '@/stores/userStore';
import { useSessionStore } from '@/stores/sessionStore';
import { createRouter, createWebHistory } from 'vue-router';
import MainWrapper from '../layouts/MainWrapper.vue';
import Home from '../components/Pages/Home.vue';
import Login from '../components/Login.vue';
import Logout from '../components/Logout.vue';
import GamePlay from '../components/Pages/GamePlay.vue';
import PasswordResetForm from '../components/PasswordResetForm.vue';
import WordWheelSequenceTester from '../components/Pages/WordWheelSequenceTester.vue';

interface Store {
  $router: Router;
}

interface Context {
  store: Store;
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: MainWrapper,
    children: [
      { path: '', name: 'home', component: Home, meta: { requiresAuth: true } },
      { path: 'login', name: 'login', component: Login },
      {
        path: 'password/reset/:token',
        name: 'password-reset',
        component: PasswordResetForm,
        meta: { requiresGuest: true },
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        redirect: { name: 'user-stats' },
        meta: { requiresAuth: true },
      },
      {
        path: 'stats',
        redirect: { name: 'user-stats' },
      },
      {
        path: 'user/stats',
        name: 'user-stats',
        component: () => import('../components/Pages/PlayerStats.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'logout',
        name: 'logout',
        component: Logout,
        meta: { requiresAuth: true },
      },
      {
        name: 'gameplay',
        path: 'play/:game',
        component: GamePlay,
        meta: { requiresAuth: true },
        props: true,
      },
      {
        name: 'puzzle-tester',
        path: 'puzzle-tester',
        component: () => import('../components/Pages/PuzzleTester.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'puzzle-train-layout-debugger',
        path: 'puzzle-train-layout-debugger',
        component: () =>
          import('../components/Pages/PuzzleTrainLayoutDebugger.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'connector-test',
        path: 'connector-test',
        component: () => import('../components/Pages/ConnectorTestPage.vue'),
      },
      {
        name: 'word-wheel-sequence-tester',
        path: 'word-wheel-tester',
        component: WordWheelSequenceTester,
      },
      {
        name: 'image-password-test',
        path: 'image-password-test',
        component: () => import('../components/Test/ImagePasswordTest.vue'),
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    redirect: '/',
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore();
  const isAuthenticated = userStore.isLoggedIn;

  // Handle audio cleanup when navigating between games or leaving games
  if (from.name === 'gameplay' || to.name === 'gameplay') {
    const { useGameAudioStore } = await import('@/stores/gameAudioStore');
    const audioStore = useGameAudioStore();

    // If navigating away from a game
    if (from.name === 'gameplay') {
      console.log('🎵 Router: Leaving game, stopping all audio');
      audioStore.stopAllAudioForTransition();
    }

    // If navigating to a game
    if (to.name === 'gameplay' && to.params.game) {
      const newGameContext = to.params.game as string;
      console.log(`🎵 Router: Preparing audio for game: ${newGameContext}`);
      await audioStore.prepareGameTransition(newGameContext);

      // Verify ticket consistency when entering a game
      if (isAuthenticated) {
        console.log(
          '🎫 Router: Verifying ticket consistency before game entry'
        );
        userStore.verifyTicketConsistency();
      }
    }
  }

  if (to.meta.requiresAuth && !isAuthenticated) {
    next({ name: 'login' });
  } else if (to.meta.requiresGuest && isAuthenticated) {
    next({ name: 'home' });
  } else if (to.name === 'login' && isAuthenticated) {
    next({ name: 'home' });
  } else {
    next();
  }
});

// router.afterEach((to, from) => {
//   console.log('Navigation complete:', { to, from });
// });

router.onError((error) => {
  console.error('Router error:', error);
});

export function createRouterPlugin() {
  return function (context: Context) {
    context.store.$router = markRaw(router);
  };
}

export const navigateToHome = () => {
  console.log('[navigateToHome] Starting navigation to home...');
  return router.push({ name: 'home' }).catch((error) => {
    console.error('[navigateToHome] Router push failed:', error);
    throw error; // Re-throw to allow exitGame to handle fallback
  });
};

export const navigateToGame = (game: string) => {
  const gamePlayStore = useGamePlayStore();
  const gameStore = useGameStore();
  const sessionStore = useSessionStore(); // Add session store

  // Reset gameExited flag when navigating to a new game
  sessionStore.setGameExited(false);

  console.log('[navigateToGame] Starting game:', game);

  // Find the game index to set lastClickedGame
  if (gameStore.games && gameStore.games.length > 0) {
    const gameIndex = gameStore.games.findIndex(
      (g) => g.short_name === game || g.url === game || g.sysname === game
    );
    if (gameIndex >= 0) {
      console.log(
        '[navigateToGame] Setting lastClickedGame to index:',
        gameIndex
      );
      gamePlayStore.setLastClickedGame(String(gameIndex));
    } else {
      console.log('[navigateToGame] Could not find game index for:', game);
      console.log(
        '[navigateToGame] Available games:',
        gameStore.games.map((g) => ({
          short_name: g.short_name,
          url: g.url,
          sysname: g.sysname,
        }))
      );
    }
  }

  gamePlayStore.state.currentGame = game;
  return router
    .push({
      name: 'gameplay',
      params: { game },
    })
    .catch((error) => {
      console.error('Navigation failed:', error);
    });
};

export default router;
